/**
 * MaintenanceSurvey - Post-completion satisfaction survey
 * Collects feedback on maintenance job quality
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Star, 
  StarHalf,
  ThumbsUp,
  ThumbsDown,
  Send,
  CheckCircle2,
  Clock,
  Wrench,
  User
} from 'lucide-react';
import api from '../lib/api';

// Star rating component
const StarRating = ({ value, onChange, label }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star 
              className={`w-7 h-7 transition-colors ${
                star <= (hovered || value)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-500 self-center">
          {value > 0 ? `${value}/5` : 'Select'}
        </span>
      </div>
    </div>
  );
};

const MaintenanceSurvey = ({ 
  maintenanceRequestId, 
  maintenanceTitle,
  contractorName,
  onComplete,
  onSkip 
}) => {
  const [loading, setLoading] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [ratings, setRatings] = useState({
    overall: 0,
    responseTime: 0,
    quality: 0,
    contractor: 0
  });
  const [comments, setComments] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);

  useEffect(() => {
    checkExistingSurvey();
  }, [maintenanceRequestId]);

  const checkExistingSurvey = async () => {
    try {
      const response = await api.get(`/maintenance/surveys/${maintenanceRequestId}`);
      if (response.data.exists) {
        setAlreadySubmitted(true);
      }
    } catch (error) {
      // Ignore errors
    }
  };

  const handleSubmit = async () => {
    if (ratings.overall === 0) {
      toast.error('Please provide an overall rating');
      return;
    }

    setLoading(true);
    try {
      await api.post('/maintenance/surveys/submit', {
        maintenance_request_id: maintenanceRequestId,
        overall_rating: ratings.overall,
        response_time_rating: ratings.responseTime || ratings.overall,
        quality_rating: ratings.quality || ratings.overall,
        contractor_rating: ratings.contractor || null,
        comments: comments || null,
        would_recommend: wouldRecommend !== false
      });

      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      
      if (onComplete) {
        setTimeout(onComplete, 1500);
      }
    } catch (error) {
      console.error('Survey submission error:', error);
      // Still show success as survey is optional
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } finally {
      setLoading(false);
    }
  };

  if (alreadySubmitted) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="py-6">
          <div className="flex items-center gap-3 text-green-700">
            <CheckCircle2 className="w-6 h-6" />
            <span>You&apos;ve already submitted feedback for this request. Thank you!</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-700">Thank You!</h3>
          <p className="text-green-600">Your feedback helps us improve our service.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          How was the service?
        </CardTitle>
        <CardDescription>
          Your feedback for: {maintenanceTitle}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Overall Rating */}
        <StarRating 
          value={ratings.overall}
          onChange={(val) => setRatings(prev => ({ ...prev, overall: val }))}
          label="Overall Experience"
        />

        {/* Response Time */}
        <div className="flex items-center gap-4">
          <Clock className="w-5 h-5 text-gray-400" />
          <StarRating 
            value={ratings.responseTime}
            onChange={(val) => setRatings(prev => ({ ...prev, responseTime: val }))}
            label="Response Time"
          />
        </div>

        {/* Quality */}
        <div className="flex items-center gap-4">
          <Wrench className="w-5 h-5 text-gray-400" />
          <StarRating 
            value={ratings.quality}
            onChange={(val) => setRatings(prev => ({ ...prev, quality: val }))}
            label="Work Quality"
          />
        </div>

        {/* Contractor Rating (if applicable) */}
        {contractorName && (
          <div className="flex items-center gap-4">
            <User className="w-5 h-5 text-gray-400" />
            <StarRating 
              value={ratings.contractor}
              onChange={(val) => setRatings(prev => ({ ...prev, contractor: val }))}
              label={`Contractor: ${contractorName}`}
            />
          </div>
        )}

        {/* Would Recommend */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Would you recommend LetWise maintenance services?
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setWouldRecommend(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                wouldRecommend === true
                  ? 'bg-green-100 border-green-500 text-green-700'
                  : 'border-gray-200 hover:border-green-300'
              }`}
            >
              <ThumbsUp className="w-4 h-4" />
              Yes
            </button>
            <button
              type="button"
              onClick={() => setWouldRecommend(false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                wouldRecommend === false
                  ? 'bg-red-100 border-red-500 text-red-700'
                  : 'border-gray-200 hover:border-red-300'
              }`}
            >
              <ThumbsDown className="w-4 h-4" />
              No
            </button>
          </div>
        </div>

        {/* Comments */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Additional Comments (Optional)
          </label>
          <Textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Tell us more about your experience..."
            rows={3}
            className="resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button 
            onClick={handleSubmit}
            disabled={loading || ratings.overall === 0}
            className="flex-1"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
          {onSkip && (
            <Button 
              variant="ghost"
              onClick={onSkip}
              className="text-gray-500"
            >
              Skip
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MaintenanceSurvey;
